import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Home = () => {
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, categoriesRes] = await Promise.all([
        api.get('/settings'),
        api.get('/categories')
      ]);
      setSettings(settingsRes.data);
      setCategories(categoriesRes.data.filter(c => c.visible));
    } catch (error) {
      console.error('Error fetching data:', error);
      setSettings({
        hero_image: 'https://customer-assets.emergentagent.com/job_traditional-textiles-2/artifacts/4dx8tv2g_WhatsApp%20Image%202026-03-04%20at%2011.22.35%20PM.jpeg',
        hero_title: 'Heritage in Every Knot',
        hero_subtitle: 'Discover the timeless art of Bandhani tie-dye, handcrafted with centuries of tradition and love.'
      });
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative h-[80vh] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(${settings?.hero_image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-7xl font-light tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {settings?.hero_title || 'Heritage in Every Knot'}
          </h1>
          <p className="text-lg md:text-xl font-light mb-8 max-w-2xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {settings?.hero_subtitle || 'Discover the timeless art of Bandhani tie-dye, handcrafted with centuries of tradition and love.'}
          </p>
          <Link to="/products">
            <Button 
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 uppercase tracking-widest text-sm font-medium"
              data-testid="hero-cta-button"
            >
              Explore Collection <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Featured Categories */}
      {categories.length > 0 && (
        <section className="py-24 md:py-32 max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-normal tracking-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Curated Collections
            </h2>
            <p className="text-lg text-muted-foreground font-light">
              Each piece tells a story of craftsmanship passed through generations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {categories.map((category) => (
              <Link key={category.id} to={`/products?category=${category.name}`} className="group text-center" data-testid={`category-${category.name.toLowerCase()}`}>
                <div className="relative overflow-hidden h-[400px] mb-4 rounded-lg">
                  <img 
                    src={category.image} 
                    alt={category.name} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors"></div>
                </div>
                <h3 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{category.name}</h3>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Craftsmanship Story */}
      {settings?.story_title && settings?.story_text && (
        <section id="story" className="bg-secondary py-24 md:py-32">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
              <div>
                {settings.story_image ? (
                  <div className="relative overflow-hidden rounded-lg bg-secondary aspect-[4/5] max-h-[600px] w-full">
                    <img
                      src={settings.story_image}
                      alt={settings.story_title}
                      onError={(e) => { e.currentTarget.src = 'https://placehold.co/800x1000/f5f5f4/a8a29e?text=Image+Unavailable'; }}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="relative overflow-hidden rounded-lg bg-secondary aspect-[4/5] max-h-[600px] w-full">
                    <img
                      src="https://images.unsplash.com/photo-1650726583448-dda0065f2f11"
                      alt="Artisan at work"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>
              <div>
                <h2 className="text-4xl md:text-5xl font-normal tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {settings.story_title}
                </h2>
                <p className="text-lg leading-relaxed font-light mb-8 whitespace-pre-line">
                  {settings.story_text}
                </p>
                <Link to="/products">
                  <Button 
                    className="border border-primary text-primary hover:bg-primary hover:text-white rounded-full px-8 py-6 uppercase tracking-widest text-sm font-medium transition-all duration-300"
                    variant="outline"
                    data-testid="story-cta-button"
                  >
                    Discover Our Work
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* How Bandhani is Made Process */}
      {settings?.process_title && settings?.process_step1?.image && (
        <section id="craftsmanship" className="py-24 md:py-32 max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-normal tracking-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {settings.process_title}
            </h2>
            {settings.process_subtitle && (
              <p className="text-lg text-muted-foreground font-light">
                {settings.process_subtitle}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Step 1 */}
            {settings.process_step1?.image && (
              <div className="space-y-4" data-testid="process-step-1">
                <div className="relative h-[350px] overflow-hidden rounded-lg">
                  <img 
                    src={settings.process_step1.image} 
                    alt={settings.process_step1.title} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-primary text-primary-foreground w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold">
                    1
                  </div>
                </div>
                <h3 className="text-2xl font-medium" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {settings.process_step1.title}
                </h3>
                <p className="text-base leading-relaxed font-light">
                  {settings.process_step1.description}
                </p>
              </div>
            )}

            {/* Step 2 */}
            {settings.process_step2?.image && (
              <div className="space-y-4" data-testid="process-step-2">
                <div className="relative h-[350px] overflow-hidden rounded-lg">
                  <img 
                    src={settings.process_step2.image} 
                    alt={settings.process_step2.title} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-primary text-primary-foreground w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold">
                    2
                  </div>
                </div>
                <h3 className="text-2xl font-medium" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {settings.process_step2.title}
                </h3>
                <p className="text-base leading-relaxed font-light">
                  {settings.process_step2.description}
                </p>
              </div>
            )}

            {/* Step 3 */}
            {settings.process_step3?.image && (
              <div className="space-y-4" data-testid="process-step-3">
                <div className="relative h-[350px] overflow-hidden rounded-lg">
                  <img 
                    src={settings.process_step3.image} 
                    alt={settings.process_step3.title} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-primary text-primary-foreground w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold">
                    3
                  </div>
                </div>
                <h3 className="text-2xl font-medium" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {settings.process_step3.title}
                </h3>
                <p className="text-base leading-relaxed font-light">
                  {settings.process_step3.description}
                </p>
              </div>
            )}

            {/* Step 4 */}
            {settings.process_step4?.image && (
              <div className="space-y-4" data-testid="process-step-4">
                <div className="relative h-[350px] overflow-hidden rounded-lg">
                  <img 
                    src={settings.process_step4.image} 
                    alt={settings.process_step4.title} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-primary text-primary-foreground w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold">
                    4
                  </div>
                </div>
                <h3 className="text-2xl font-medium" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {settings.process_step4.title}
                </h3>
                <p className="text-base leading-relaxed font-light">
                  {settings.process_step4.description}
                </p>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;